import {
  Component,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ServicePricePopupComponent } from '../../../../../../shared/components/service-price-popup/service-price-popup.component';

import { IServicePriceItems } from '../../../../../../shared/services/services/services/services.service';
import { combineLatest, filter, Subscription, take } from 'rxjs';
import { select, Store } from '@ngrx/store';
import {
  selectBlackList,
  selectCaseApproved,
  selectCaseCreateDate,
  selectReadonly,
} from '../../../../store/selectors/common-case.selector';
import {
  ICaseRowRule,
  IServiceInformation,
  IServiceInformationItem,
} from '../../interfaces/case-card-interfaces';
import { CorePopupComponent } from '../../../../../../ui-components/core-popup/core-popup.component';
import { AppMessageService } from '../../../../../../shared/services/app-message/app-message.service';
import {
  selectDropdownLoaded,
  selectDropdownOptions,
} from '../../../../store/selectors/dropdowns.selector';
import { selectServiceInformations } from '../../../../store/selectors/service-informations.selector';
import { selectCasePriceType } from '../../../../store/selectors/case-informations.selector';
import { AppAlertService } from '../../../../../../shared/services/app-alert/app-alert.service';
import { TranslateService } from '@ngx-translate/core';
import { Option } from '../../../../../../ui-components/core-dropdown/core-dropdown.component';
import Decimal from 'decimal.js';

export interface Summary {
  serviceCount: number | null;
  provisionNumber: string | null;
  provisionCompany: string | null;
  provisionNote: string | null;
  totalPrice: number | null;
  totalProvisionPrice: number | null;
  totalDiscountPrice: number | null;
  totalBottomDiscountPrice: number | null;
  lastPrice: number | null;
  bottomDiscountDescription:string | null;
}
@Component({
  selector: 'app-service-informations',
  templateUrl: './service-informations.component.html',
  styleUrl: './service-informations.component.scss',
})
export class ServiceInformationsComponent implements OnInit, OnDestroy {
  @ViewChild('servicePopup') servicePopup: ServicePricePopupComponent;
  @ViewChild('rowDiscountPopup') rowDiscountPopup: CorePopupComponent;
  @ViewChild('flexiblePricePopup') flexiblePricePopup: CorePopupComponent;
  @ViewChild('bottomDiscountPopup') bottomDiscountPopup: CorePopupComponent;
  @ViewChild('provisionPopup') provisionPopup: CorePopupComponent;
  @Output() formChanged = new EventEmitter();

  private subscription!: Subscription;
  isReadOnly: boolean = false;
  readonlyInformations: IServiceInformation | null;
  blackList: string[] = [];
  priceType:  Option| null = null;
  isFooterExpanded: boolean = false;
  searchText: string = '';

  lastScrollTop = 0;
  info: IServiceInformation = {
    services: [],
    numberOfServices: null,
    provisionNo: null,
    provisionInstitution: null,
    provisionDescription: null,
    totalPrice: null,
    totalProvisionPrice: null,
    totalDiscountPrice: null,
    bottomDiscountPrice: null,
    lastPrice: null,
    bottomDiscountDescription:null,
  };

  rowDiscount: {
    price: number;
    percentage: number;
    discountPrice: number;
    finalPrice: number;
    radiobutton: 'percentage' | 'price' | null;
    calculating: 'amount' | 'percentage' | null;
  } = {
    price: 0,
    percentage: 0,
    discountPrice: 0,
    finalPrice: 0,
    calculating: null,
    radiobutton: null,
  };

  bottomDiscount: {
    price: number;
    percentage: number;
    discountPrice: number;
    finalPrice: number;
    radiobutton: 'rate' | 'total' | null;
    calculating: 'amount' | 'percentage' | null;
    description:string| null,
  } = {
    price: 0,
    percentage: 0,
    discountPrice: 0,
    finalPrice: 0,
    calculating: null,
    radiobutton: null,
    description: null,
  };

  flexiblePrice: number = 0;
  provision: {
    no: string | null;
    institution: number | null;
    description: string | null;
    services: IServiceInformationItem[];
    selectedServices: IServiceInformationItem[];
    percentage: number | null;
    discountPrice: number | null;
    selectedOption: 'percentage' | 'price' | null;
  } = {
    no: null,
    institution: null,
    description: null,
    services: [],
    selectedServices: [],
    percentage: null,
    discountPrice: null,
    selectedOption: 'percentage',
  };
  selectedRow: IServiceInformationItem | null = null;
  institutions: Option[];
  provionalInstitutions: Option[];
  caseCreatedAt: Date | null;
  filteredServices: IServiceInformationItem[] = [];
  showRemoveButton: boolean = false;
  isCaseApproved: boolean | null;

  constructor(
    private readonly store: Store,
    private readonly messageService: AppMessageService,
    private readonly appAlertService: AppAlertService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.store.pipe(select(selectDropdownOptions('institutions'))),
      this.store.pipe(select(selectDropdownLoaded)),
    ])
      .pipe(
        // loaded flag'ı true olduğunda (yani veriler yüklendiğinde) veri akışı devam eder
        filter(([institutions, loaded]) => loaded),
        take(1)
      )
      .subscribe(([institutions, loaded]) => {
        this.institutions = institutions;
        this.provionalInstitutions = institutions.filter(
          (e) => e?.provisionable
        );
      });

    this.subscription = combineLatest([
      this.store.select(selectCasePriceType),
      this.store.select(selectCaseCreateDate),
      this.store.select(selectServiceInformations),
      this.store.select(selectBlackList),
      this.store.select(selectReadonly),
      this.store.select(selectCaseApproved),

    ]).subscribe({
      next: ([
        priceType,
        caseCreatedAt,
        serviceInformations,
        blackList,
        isReadOnly,
        isApproved
      ]) => {
        this.priceType = priceType;
        this.isReadOnly = isReadOnly;
        this.caseCreatedAt = caseCreatedAt;
        this.isCaseApproved = isApproved ?? false;

        this.info = serviceInformations
          ? {
              ...serviceInformations,
              services:
                serviceInformations?.services?.map((s) => {
                  const isExpended = this.info.services.find(
                    (service) => service.serviceId === s.serviceId
                  )?.expanded;
                  return {
                    ...s,
                    discount: { ...s.discount },
                    bottomDiscount: { ...s.bottomDiscount },
                    provision: { ...s.provision },
                    ruleOfPopups: { ...s.ruleOfPopups },
                    flexiblePrice: { ...s.flexiblePrice },
                    expanded: isExpended ?? false,
                  };
                }) ?? [],
            }
          : {
              services: [],
              numberOfServices: null,
              provisionNo: null,
              provisionInstitution: null,
              provisionDescription: null,
              totalPrice: null,
              totalProvisionPrice: null,
              totalDiscountPrice: null,
              bottomDiscountPrice: null,
              lastPrice: null,
              bottomDiscountDescription:null,
            };

        this.onSearch();
        this.calculateProcess(false);

        this.blackList = blackList;
      },
    });
  }

  onSaveServices(response: {
    services: IServicePriceItems[];
    totalPrice: number;
    numberOfService: number;
  }) {
    //daha önceden var olanı tekrar seçerse onları dahil etmemesi için filtrele
    const filteredServices =
      response.services.filter(
        (s) => !this.info.services.some((sp) => sp.serviceId === s.serviceId)
      ).map(e=>{
        return {
          ...e,
          bottomDiscount: {
            percentage: this.info.services.find(bottomDiscount=>bottomDiscount.bottomDiscount.percentage)?.bottomDiscount?.percentage ?? null,
            price: null,
            description: this.info.services.find(bottomDiscount=>bottomDiscount.bottomDiscount.description)?.bottomDiscount?.description ?? null,
          },
        }
      }) ?? [];
    this.info.services = [
      ...this.info.services,
      ...filteredServices.map((elem) => {
        return {
          serviceId: elem?.serviceId,
          serviceName: elem?.serviceName,
          serviceCode: elem?.serviceCode,
          quantity: elem?.quantity!,
          gatePrice: elem?.gatePrice,
          memberPrice: elem?.memberPrice,
          totalPrice: elem.total!,
          unitPrice: elem.unitPrice!,
          lastPrice: elem.total!,
          expanded: false,
          priceType: elem?.priceType ?? null,
          discount: {
            percentage: null,
            price: null,
          },
          bottomDiscount: {
            percentage: elem?.bottomDiscount?.percentage ?? null,
            price: null,
            description: elem?.bottomDiscount?.description?? null
          },
          provision: {
            percentage: null,
            price: null,
          },
          flexiblePrice: {
            isFlexible:
              elem.priceType === 'memberPrice'
                ? elem.memberPrice?.isFlexiblePrice ?? false
                : elem.gatePrice?.isFlexiblePrice ?? false,
            price: null,
          },
          givenForFree:
            elem.priceType === 'memberPrice'
              ? elem.memberPrice?.givenForFree ?? false
              : elem.gatePrice?.givenForFree ?? false,
          ruleOfPopups: {
            rowDiscount: true,
            bottomDiscount: true,
            provision: elem.priceType === 'memberPrice',
            flexiblePrice:
              elem.priceType === 'memberPrice'
                ? elem.memberPrice?.isFlexiblePrice ?? false
                : elem.gatePrice?.isFlexiblePrice ?? false,
            firstProcess: null,
          },
        };
      }),
    ];

    this.onSearch();
    this.calculateProcess();
  }

  toDecimalSafe(value: number | null): Decimal {
    return new Decimal(value ?? 0); // null ise 0 olarak kabul et
  }

  calculateProcess(isChanged = true) {
    //son tutarı bul
    //1. miktar ile birim fiyatı çarp
    //2.provizyon varmı kontrol et
    //3.satır indirimi varmı
    //4.dip inidirmi varmı
    for (const service of this.info.services) {

      //eğer esnek fiyatlı ise direkt tutar olarak esnek fiyatı al
      service.totalPrice = service?.flexiblePrice?.price
        ? this.toDecimalSafe(service?.flexiblePrice?.price).toNumber()
        : this.toDecimalSafe(service.unitPrice)
          ?.times(this.toDecimalSafe(service.quantity))
          .toNumber();


      service.lastPrice = service?.totalPrice;

      if (service?.provision?.percentage) {

        const provisionRate = this.toDecimalSafe(service.provision.percentage);
        const difference = this.toDecimalSafe(service.lastPrice)
          .times(provisionRate)
          .div(100);
        service.provision.price = difference.toNumber();
        service.lastPrice = this.toDecimalSafe(service.lastPrice)
          .minus(difference)
          .toNumber();
      }
      if (service?.discount?.percentage) {
        /* güncel son toplam fiyattan indirim uygulanmış halini bulmak için
        toplam fiyatın indirim yüzdesi ile indirim yapılacak miktarı bul
        güncel indirim tutarı bulunan sonuctur.
        son tutar kendisi - difference tır.
        */

        const discountRate = this.toDecimalSafe(service.discount.percentage);
        const difference = this.toDecimalSafe(service.lastPrice)
          .times(discountRate)
          .div(100);
        service.discount.price = difference.toNumber();
        service.lastPrice = this.toDecimalSafe(service.lastPrice)
          .minus(difference)
          .toNumber();
      }
      if (service?.bottomDiscount?.percentage) {

        const bottomDiscountRate = this.toDecimalSafe(
          service.bottomDiscount.percentage
        );
        const difference = this.toDecimalSafe(service.lastPrice)
          .times(bottomDiscountRate)
          .div(100);
        service.bottomDiscount.price = difference.toNumber();
        service.lastPrice = this.toDecimalSafe(service.lastPrice)
          .minus(difference)
          .toNumber();
      }
      service.ruleOfPopups = this.checkPopupRule(service);
    }
    if (isChanged) {
      this.servicesChanged();
    }
    this.calculateSummary();
  }

  calculateSummary() {
    const services = this.info.services ?? [];
    this.info.numberOfServices = services.length;

    this.info.totalPrice =
      services
        .reduce(
          (sum, item) => this.toDecimalSafe(item.totalPrice).plus(sum),
          new Decimal(0)
        )
        .toNumber() ?? null;

    this.info.lastPrice =
      services
        .reduce(
          (sum, item) => this.toDecimalSafe(item.lastPrice).plus(sum),
          new Decimal(0)
        )
        .toNumber() ?? null;

    this.info.totalDiscountPrice =
      services
        .reduce(
          (sum, item) => this.toDecimalSafe(item.discount.price).plus(sum),
          new Decimal(0)
        )
        .toNumber() ?? null;

    this.info.totalProvisionPrice =
      services
        .reduce(
          (sum, item) => this.toDecimalSafe(item.provision.price).plus(sum),
          new Decimal(0)
        )
        .toNumber() ?? null;

    this.info.bottomDiscountPrice =
      services
        .reduce(
          (sum, item) =>
            this.toDecimalSafe(item.bottomDiscount.price).plus(sum),
          new Decimal(0)
        )
        .toNumber() ?? null;


    if (!this.info.bottomDiscountDescription) {
      this.info.bottomDiscountDescription =
        services.find(s => s.bottomDiscount?.description)?.bottomDiscount?.description ?? null;
    }


  }

  private checkPopupRule(service: IServiceInformationItem): ICaseRowRule {
    const { ruleOfPopups, priceType } = service;

    //ilk işlem boş ise olduğu gibi döndür
    if (ruleOfPopups.firstProcess === null) {
      return ruleOfPopups;
    }
    /*
    İlk işlem satır indirimi ise yada dip indirim ise
    dip indirim yapabilir
    satır indirimi yapılabilir
    provizyon yapamaz
    esnek fiyat yapamaz
    */
    if (ruleOfPopups.firstProcess === 'row') {
      ruleOfPopups.bottomDiscount = true;
      ruleOfPopups.provision = false;
      ruleOfPopups.flexiblePrice = false;
      ruleOfPopups.rowDiscount = true;
    }

    if (ruleOfPopups.firstProcess === 'bottom') {
      ruleOfPopups.bottomDiscount = true;
      ruleOfPopups.provision = false;
      ruleOfPopups.flexiblePrice = false;
      ruleOfPopups.rowDiscount = false;
    }

    /*
    İlk işlem provizyon ise
    satır indirim yapabilir
    dip indirim yapabilir
    provizyon yapılabilir
    esnek fiyat yapamaz
    */
    if (ruleOfPopups.firstProcess === 'provision') {
      ruleOfPopups.bottomDiscount = true;
      ruleOfPopups.provision = priceType === 'memberPrice';
      ruleOfPopups.rowDiscount = true;
      ruleOfPopups.flexiblePrice = false;
    }

    /*
    İlk işlem esnek fiyat girişi ise
    satır indirim yapabilamaz
    dip inidirim yapılabilir
    provizyon yapılamaz
    esnek fiyat yapabilir
    */
    if (ruleOfPopups.firstProcess === 'flexible') {
      ruleOfPopups.bottomDiscount = true;
      ruleOfPopups.provision = false;
      ruleOfPopups.rowDiscount = false;
      ruleOfPopups.flexiblePrice = true;
    }
    return ruleOfPopups;
  }

  toggleExpand(service: IServiceInformationItem): void {
    service.expanded = !service.expanded;
  }

  deleteService(id: number): void {
    this.appAlertService.sweetConfirm({
      header: this.translate.instant('case.service-informations.delete-header'),
      message: this.translate.instant('case.service-informations.delete-text'),
      showCloseButton: true,
      accept: () => {
        this.filteredServices = this.filteredServices.filter(
          (item) => item.serviceId !== id
        );
        this.info.services = this.info.services.filter(
          (item) => item.serviceId !== id
        );
        this.calculateProcess();
      },
    });
  }

  updateServicePriceItemQuantity(service: IServiceInformationItem): void {
    const quantity = this.toDecimalSafe(service?.quantity ?? 1);
    const unitPrice = this.toDecimalSafe(service?.unitPrice);
    const flexiblePrice =
      service?.flexiblePrice?.price === null
        ? null: this.toDecimalSafe(service?.flexiblePrice?.price);
    // Eğer flexiblePrice > 0 ise onu kullan, yoksa unitPrice * quantity
    const totalPrice = flexiblePrice ??  unitPrice.times(quantity)


    service.quantity = quantity.toNumber();
    service.totalPrice = totalPrice.toNumber();
    service.lastPrice = totalPrice.toNumber();

    let lastPrice = totalPrice;
    if (service.provision.percentage) {
      const rate = this.toDecimalSafe(service.provision.percentage);
      const difference = lastPrice.times(rate).div(100);
      service.provision.price = difference.toNumber();
      service.lastPrice = lastPrice.minus(difference).toNumber();
    }
    if (service.discount.percentage) {
      const rate = this.toDecimalSafe(service.discount.percentage);
      const difference = lastPrice.times(rate).div(100);
      service.discount.price = difference.toNumber();
      service.lastPrice = lastPrice.minus(difference).toNumber();
    }
    if (service.bottomDiscount.percentage) {
      const rate = this.toDecimalSafe(service.bottomDiscount.percentage);
      const difference = lastPrice.times(rate).div(100);
      service.bottomDiscount.price = difference.toNumber();
      service.lastPrice = lastPrice.minus(difference).toNumber();
    }
    this.servicesChanged();
    this.calculateSummary();
  }

  openServicePopup() {
    this.servicePopup.initilize();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset ?? document.documentElement.scrollTop;

    if (documentHeight - (scrollTop + windowHeight) < 30) {
      this.isFooterExpanded = true;
    } else if (scrollTop < this.lastScrollTop) {
      // Yukarı kaydırılıyorsa footer'ı küçült
      this.isFooterExpanded = false;
    }

    this.lastScrollTop = Math.max(0, scrollTop);
  }

  toggleFooter() {
    this.isFooterExpanded = !this.isFooterExpanded;
  }

  openRowDiscountPopup(service: IServiceInformationItem) {
    this.selectedRow = service;

    //indirim yapıcagı oran son tutar + daha önce yapıldı ise satır indirimi + dip indirimi olmalı
    //böylelikle indirim üzerine indirim yapılmasını engeller sıfırdan indirim yapıyor gibi davranır

    const lastPrice = this.toDecimalSafe(service?.lastPrice);
    const discountPrice = this.toDecimalSafe(service?.discount?.price);
    const bottomDiscountPrice = this.toDecimalSafe(
      service?.bottomDiscount?.price
    );
    const price = lastPrice.plus(discountPrice).plus(bottomDiscountPrice);
    const finalPrice = lastPrice.plus(discountPrice);
    this.rowDiscount = {
      price: price.toNumber(),
      percentage: service?.discount?.percentage ?? 0,
      discountPrice: service?.discount?.price ?? 0,
      finalPrice: finalPrice.toNumber(),
      calculating: null,
      radiobutton: null,
    };

    this.calculateRowFromPercentage();
    if (this.rowDiscount.percentage) {
      this.showRemoveButton = true;
    }
    this.rowDiscountPopup.openDialog();
  }

  calculateRowFromPercentage() {
    this.rowDiscount.calculating = 'percentage';
    const price = this.toDecimalSafe(this.rowDiscount.price);
    const percentage = this.toDecimalSafe(this.rowDiscount.percentage);
    if (price.gt(0)) {
      const discountAmount = price.times(percentage).div(100);
      const finalPrice = price.minus(discountAmount);
      this.rowDiscount.discountPrice = discountAmount.toNumber();
      this.rowDiscount.finalPrice = finalPrice.toNumber();
    }
    this.rowDiscount.calculating = null;
  }

  calculateRowFromDiscountPrice() {
    this.rowDiscount.calculating = 'amount';
    const price = this.toDecimalSafe(this.rowDiscount.price);
    const discount = this.toDecimalSafe(this.rowDiscount.discountPrice);
    if (price.gt(0)) {
      const percentage = discount.div(price).times(100);
      const finalPrice = price.minus(discount);

      this.rowDiscount.percentage = percentage.toNumber();
      this.rowDiscount.finalPrice = finalPrice.toNumber();
    }
    this.rowDiscount.calculating = null;
  }

  saveRowDiscount() {
    const service = this.info.services.find(
      (s) => s.serviceId === this.selectedRow!.serviceId
    );

    if (service) {
      if (this.rowDiscount.discountPrice === 0) {
        this.removeRowDiscount();
        return;
      } else {
        service.discount = {
          percentage: this.rowDiscount.percentage,
          price: this.rowDiscount.discountPrice,
        };
        //artık first process bizim için lastProcess oldu
        // if (service.ruleOfPopups.firstProcess === null) {
        service.ruleOfPopups.firstProcess = 'row';
        // }
      }
    }
    this.messageService.successToast(
      this.translate.instant('common.success'),
      this.translate.instant('case.service-informations.success-discount')
    );
    this.calculateProcess();
    this.rowDiscountPopup.closeDialog();
  }

  removeRowDiscount() {
    const service = this.info.services.find(
      (s) => s.serviceId === this.selectedRow!.serviceId
    );

    if (service) {
      service.discount = {
        percentage: null,
        price: null,
      };
      service.ruleOfPopups = {
        firstProcess: null,
        rowDiscount: service.flexiblePrice.price === null,
        provision:
          service.priceType === 'memberPrice' &&
          service.discount.price === null &&
          service.flexiblePrice.price === null &&
          service.bottomDiscount.price === null,
        bottomDiscount: true,
        flexiblePrice:
          service.flexiblePrice.isFlexible &&
          service.provision.price === null &&
          service.bottomDiscount.price === null &&
          service.discount.price === null,
      };
    }
    this.messageService.infoToast(
      this.translate.instant('common.success'),
      this.translate.instant('case.service-informations.remove-discount')
    );
    this.calculateProcess();
    this.showRemoveButton = false;
    this.rowDiscountPopup.closeDialog();
  }

  closeRowDiscount() {
    this.selectedRow = null;
    this.showRemoveButton = false;
    this.rowDiscount.radiobutton = null;
  }

  openFlexiblePricePopup(item: IServiceInformationItem) {
    this.selectedRow = item;
    this.flexiblePrice = item.flexiblePrice.price ?? 0;
    if (this.flexiblePrice !== null && this.flexiblePrice !== undefined) {
      this.showRemoveButton = true;
    }
    this.flexiblePricePopup.openDialog();
  }

  saveFlexiblePricePopup() {
    const service = this.info.services.find(
      (s) => s.serviceId === this.selectedRow!.serviceId
    );
    if (service) {
      //esnek fiyat 0 tl yapılmış ve hizmet ücretsiz verilemez ise izin verme
      if (this.flexiblePrice === 0 && !service.givenForFree) {
        this.messageService.errorToast(
          'Başarısız',
          'Hizmet ücretsiz verilemez.'
        );
        this.flexiblePricePopup.closeDialog();
        return;
      }
      service.totalPrice = this.flexiblePrice;
      service.flexiblePrice.price = this.flexiblePrice;
      service.ruleOfPopups.firstProcess = 'flexible';
      this.calculateProcess();
    }
    this.messageService.successToast(
      this.translate.instant('common.success'),
      this.translate.instant('case.service-informations.success-flexible-price')
    );
    this.flexiblePricePopup.closeDialog();
  }

  removeFlexible() {
    const service = this.info.services.find(
      (s) => s.serviceId === this.selectedRow!.serviceId
    );
    if (service) {
      service.flexiblePrice.price = null;
      service.ruleOfPopups = {
        firstProcess: null,
        rowDiscount: true,
        provision:
          service.priceType === 'memberPrice' &&
          service.discount.price === null &&
          service.flexiblePrice.price === null &&
          service.bottomDiscount.price === null,
        bottomDiscount: true,
        flexiblePrice:
          service.flexiblePrice.isFlexible &&
          service.provision.price === null &&
          service.bottomDiscount.price === null &&
          service.discount.price === null,
      };
      this.calculateProcess();
      this.showRemoveButton = false;
      this.flexiblePricePopup.closeDialog();
    }
    this.messageService.infoToast(
      this.translate.instant('common.success'),
      this.translate.instant('case.service-informations.remove-flexible-price')
    );
  }
  closeFlexiblePricePopup() {
    this.flexiblePrice = 0;
    this.showRemoveButton = false;
  }

  openBottomDiscountPopup() {
    //daha önce yapılmış dip indirimleri geri ekle
    //daha önce yapılan yüzdelik oranı bul

    const totalPrice = this.info.services.reduce(
      (sum, item) =>
        sum
          .plus(this.toDecimalSafe(item?.lastPrice))
          .plus(this.toDecimalSafe(item?.bottomDiscount?.price)),
      new Decimal(0)
    );

    this.bottomDiscount = {
      price: totalPrice.toNumber(),
      percentage:
        this.info.services.length > 0 &&
        this.info.services[0].bottomDiscount?.percentage
          ? this.info.services[0].bottomDiscount?.percentage
          : 0,
      discountPrice: 0,
      finalPrice: totalPrice.toNumber(),
      calculating: null,
      radiobutton: null,
      description: this.info.bottomDiscountDescription ??
                 this.info.services[0]?.bottomDiscount?.description ?? null
    };
    this.calculateBottomFromPercentage();
    if (this.bottomDiscount.percentage) {
      this.showRemoveButton = true;
    }
    this.bottomDiscountPopup.openDialog();
  }

  calculateBottomFromPercentage() {
    this.bottomDiscount.calculating = 'percentage';

    const price = this.toDecimalSafe(this.bottomDiscount.price);
    const percentage = this.toDecimalSafe(this.bottomDiscount.percentage);
    const description = this.bottomDiscount.description;

    if (price.gt(0)) {
      const discountAmount = price.times(percentage).div(100);
      const finalPrice = price.minus(discountAmount);

      this.bottomDiscount.discountPrice = discountAmount.toNumber();
      this.bottomDiscount.finalPrice = finalPrice.toNumber();
      this.bottomDiscount.description = description;
    }

    this.bottomDiscount.calculating = null;
  }

  calculateBottomFromDiscountPrice() {
    this.bottomDiscount.calculating = 'amount';
    const price = this.toDecimalSafe(this.bottomDiscount.price);
    const discount = this.toDecimalSafe(this.bottomDiscount.discountPrice);
    if (price.gt(0)) {
      const percentage = discount.div(price).times(100);
      const finalPrice = price.minus(discount);
      this.bottomDiscount.percentage = percentage.toNumber();
      this.bottomDiscount.finalPrice = finalPrice.toNumber();
    }
    this.bottomDiscount.calculating = null;
  }

  saveBottomDiscount() {
    if (this.bottomDiscount.percentage === 0) {
      this.removeBottomDiscount();
      return;
    } else {
      for(const s of this.info.services){

        s.bottomDiscount.percentage = this.bottomDiscount.percentage;
        s.ruleOfPopups.firstProcess = 'bottom';
        s.bottomDiscount.description=this.bottomDiscount.description;
      }
      this.info.bottomDiscountDescription = this.bottomDiscount.description;
    }

    this.calculateProcess();
    this.messageService.successToast(
      this.translate.instant('common.success'),
      this.translate.instant(
        'case.service-informations.success-bottom-discount'
      )
    );
    this.showRemoveButton = false;
    this.bottomDiscountPopup.closeDialog();
  }

  closeBottomDiscount() {
    this.bottomDiscount.radiobutton = null;
  }
  removeBottomDiscount() {
    for(const s of this.info.services){
      s.bottomDiscount.percentage = null;
      s.bottomDiscount.price = null;
      s.bottomDiscount.description = null;
      s.ruleOfPopups = {
        firstProcess: null,
        rowDiscount: s.flexiblePrice.price === null,
        provision:
          s.priceType === 'memberPrice' &&
          s.discount.price === null &&
          s.flexiblePrice.price === null &&
          s.bottomDiscount.price === null,
        bottomDiscount: true,
        flexiblePrice:
          s.flexiblePrice.isFlexible &&
          s.provision.price === null &&
          s.bottomDiscount.price === null &&
          s.discount.price === null,
      };
    };

    this.info.bottomDiscountDescription = null;

    this.calculateProcess();
    this.messageService.infoToast(
      this.translate.instant('common.success'),
      this.translate.instant('case.service-informations.remove-bottom-discount')
    );
    this.showRemoveButton = false;
    this.bottomDiscountPopup.closeDialog();
  }

  openProvisionPopup() {
    const services = this.info.services.map((s) => {
      return {
        ...s,
        lastPrice: s.lastPrice,
        provision: {
          ...s.provision,
        },
      };
    });

    const filteredServices =
      services.filter(
        (s) =>
          s.priceType === 'memberPrice' && s.ruleOfPopups.provision === true
      ) ?? [];

    if (filteredServices.length === 0) {
      this.messageService.warningToast(
        this.translate.instant('case.service-informations.no-process'),
        this.translate.instant('case.service-informations.no-provision')
      );
      return;
    }
    this.provision = {
      no: this.info.provisionNo,
      institution: this.info.provisionInstitution,
      description: this.info.provisionDescription,
      percentage: null,
      discountPrice: null,
      selectedOption: 'percentage',
      services: filteredServices,
      selectedServices: filteredServices.filter(
        (s) => s.provision.percentage !== null
      ),
    };

    this.provision.selectedServices = this.provision.services;
    this.provision.selectedOption = 'percentage';
    this.provision.percentage=100;
    this.applyProvisionAll()

    this.provisionPopup.openDialog();
  }

  calculateProvisionFromPercentage(service: IServiceInformationItem) {
    const totalPrice = this.toDecimalSafe(service.totalPrice);
    const percentage = this.toDecimalSafe(service?.provision?.percentage);

    if (totalPrice.gt(0)) {
      const provisionAmount = totalPrice.times(percentage).div(100);
      service.provision.price = provisionAmount.toNumber();
      const lastPrice = totalPrice.minus(provisionAmount);
      service.lastPrice = lastPrice.toNumber();
    }
  }

  calculateProvisionFromPrice(service: IServiceInformationItem) {
    const totalPrice = this.toDecimalSafe(service.totalPrice);
    const provisionPrice = this.toDecimalSafe(service?.provision?.price);
    if (totalPrice.gt(0)) {
      const percentage = provisionPrice.div(totalPrice).times(100);
      const lastPrice = totalPrice.minus(provisionPrice);
      service.provision.percentage = percentage.toNumber();
      service.lastPrice = lastPrice.toNumber();
    }
  }

  applyProvisionAll() {
    if (this.provision.selectedServices.length === 0) {
      this.messageService.warningToast(
        this.translate.instant('case.service-informations.not-selected'),
        this.translate.instant('case.service-informations.not-selected-detail')
      );
      return;
    }
    if (this.provision.selectedOption === 'percentage') {
      for(const s of this.provision.selectedServices){
        s.provision.percentage = this.provision.percentage;
        this.calculateProvisionFromPercentage(s);
      }
    } else {
      const total =
        this?.provision?.selectedServices?.reduce(
          (sum, item) => sum.plus(this.toDecimalSafe(item.totalPrice)),
          new Decimal(0)
        ).toNumber() ?? 1;
      const discount = this.toDecimalSafe(this.provision?.discountPrice);
      //indirilmek istenilen tutar , toplam tutarın yüzde kaçı onu bul
      //ve o oranı bütün seçili hizmetlerden düş
      const rate = discount.times(100).div(total).toNumber();
      for(const s of this.provision.selectedServices){

        s.provision.percentage = rate;
        this.calculateProvisionFromPercentage(s);
      }
    }
  }

  clearProvisionAll() {
    const services = this.info.services.map((s) => {
      return {
        ...s,
        lastPrice: s.totalPrice,
        provision: {
          ...s.provision,
        },
      };
    });

    this.provision = {
      ...this.provision,
      percentage: null,
      discountPrice: null,
      services: services.filter(
        (s) => s.priceType !== 'gatePrice' || s.ruleOfPopups.provision === false
      ),
      selectedServices: services.filter((s) => s.provision.percentage !== null),
    };
  }

  saveProvisionPopup() {
    this.info.provisionNo = this.provision.no;
    this.info.provisionInstitution = this.provision.institution;
    this.info.provisionDescription = this.provision.description;
    const removedProvisions =
      this.provision.services.filter(
        (s) => s?.provision?.percentage === null
      ) ?? [];
    const updatedProvisions =
      this.provision.selectedServices.filter(
        (s) => s?.provision?.percentage !== null
      ) ?? [];

    if (!this.provision.institution || !this.provision?.no?.trim()) {
      this.messageService.errorToast(
        this.translate.instant('common.error'),
        this.translate.instant('case.service-informations.provision-validation')
      );
      return;
    }
    //provizyonu kalkanları ana listedende kaldı
    for(const elem of removedProvisions){
      const service = this.info.services.find(
        (e) => elem.serviceId === e.serviceId
      );
      if (service) {
        service.provision = {
          percentage: null,
          price: null,
        };
        service.ruleOfPopups = {
          firstProcess: null,
          rowDiscount: service.flexiblePrice.price === null,
          provision:
            service.priceType === 'memberPrice' &&
            service.discount.price === null &&
            service.flexiblePrice.price === null &&
            service.bottomDiscount.price === null,
          bottomDiscount: true,
          flexiblePrice:
            service.flexiblePrice.isFlexible &&
            service.provision.price === null &&
            service.bottomDiscount.price === null &&
            service.discount.price === null,
        };
      }
    }

    for(const elem of updatedProvisions){
      const service = this.info.services.find(
        (e) => elem.serviceId === e.serviceId
      );
      if (service) {
        if (elem.provision.percentage === 0) {
          service.provision = {
            percentage: null,
            price: null,
          };
          service.ruleOfPopups = {
            firstProcess: null,
            rowDiscount: service.flexiblePrice.price === null,
            provision:
              service.priceType === 'memberPrice' &&
              service.discount.price === null &&
              service.flexiblePrice.price === null &&
              service.bottomDiscount.price === null,
            bottomDiscount: true,
            flexiblePrice:
              service.flexiblePrice.isFlexible &&
              service.provision.price === null &&
              service.bottomDiscount.price === null &&
              service.discount.price === null,
          };
        } else {
          service.provision = {
            percentage: elem.provision.percentage,
            price: elem.provision.price,
          };
          service.ruleOfPopups.firstProcess = 'provision';
        }
      }
    }

    this.messageService.successToast(
      this.translate.instant('common.success'),
      this.translate.instant('case.service-informations.success-provision')
    );
    this.calculateProcess();
    this.provisionPopup.closeDialog();
  }

  resetProvisionPopupDatas() {
    this.provision = {
      ...this.provision,
      percentage: null,
      discountPrice: null,
      services: [],
      selectedServices: [],
    };
  }

  provisionSelectionRemoved(e: any) {
    const removedProvision = this.provision.services.find(
      (s) => s.serviceId === e?.data?.serviceId
    );

    if (removedProvision) {
      const lastPrice = this.toDecimalSafe(removedProvision?.lastPrice);
      const provisionPrice = this.toDecimalSafe(removedProvision?.provision?.price);
      removedProvision.lastPrice = lastPrice.plus(provisionPrice).toNumber();
      removedProvision.provision = {
        percentage: null,
        price: null,
      };
    }
  }

  onProvisionSelectionChange(e: any) {
    if (!e?.checked) {
      for(const elem of this.provision.services){
         elem.provision = {
          percentage: null,
          price: null,
        };
      }
    }
  }

  get minProvisionTotalPrice(): number {
    return this.provision?.services?.length
      ? Math.min(
          ...this.provision.services.map((service) => service.totalPrice)
        )
      : 0;
  }

  get maxProvisionTotalPrice(): number {
    const total = (this?.provision?.selectedServices ?? []).reduce(
      (sum, item) => sum.plus(this.toDecimalSafe(item?.totalPrice)),
      new Decimal(0)
    );

    return total.toNumber();
  }

  get provisionInstitution(): string | null {
    return (
      this.institutions.find((e) => e.value === this.info.provisionInstitution)
        ?.label ?? null
    );
  }

  radioButtonChange() {
    if (this.provision.selectedOption === 'percentage') {
      this.provision.discountPrice = null;
    } else {
      this.provision.percentage = null;
    }
  }

  getFormValues() {
    if (this.info.services.length === 0) {
      return;
    }
    return this.info;
  }

  servicesChanged() {
    this.formChanged.emit();
  }

  onSearch() {
    this.filteredServices = this.info.services;
    this.filteredServices = this.filteredServices.filter((s) =>
      s?.serviceName
        ?.toLocaleLowerCase()
        ?.includes(this.searchText?.toLocaleLowerCase())
    );
  }
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: Event): void {
    this.unSub();
  }

  unSub() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
  ngOnDestroy(): void {
    this.unSub();
  }
}
